// src/features/business-functions/hooks/useAccountWorkspaces.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { accountService } from '../services/accountService';
import { adaptWorkspaceList, extractWorkspaceList } from '../adapters/workspaceListAdapter';
import { CAMPAIGN_COPY } from '../constants/campaignCopy';
import { accountWorkspacesAtom } from '../state/accountAtoms';

/**
 * Fetches and manages the workspace list for a specific account.
 * Separates data logic from UI — components only receive final workspace models.
 *
 * @param {string} accountId
 */
export default function useAccountWorkspaces(accountId) {
  const [workspacesAtom, setWorkspacesAtom] = useRecoilState(accountWorkspacesAtom);
  const loadedAccountRef = useRef(null);

  const [workspaces, setWorkspaces] = useState(workspacesAtom.workspaces || []);
  const [totalCount, setTotalCount] = useState(workspacesAtom.total_count || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadedAccountRef.current = null;
    setWorkspaces([]);
    setTotalCount(0);
    setError(null);
  }, [accountId]);

  const fetchWorkspaces = useCallback(async () => {
    if (!accountId) {
      setWorkspaces([]);
      setTotalCount(0);
      setError(null);
      return;
    }

    const isFirstLoadForAccount = loadedAccountRef.current !== accountId;
    if (isFirstLoadForAccount) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const res = await accountService.getAccountWorkspaces(accountId, { search });
      if (res?.status === 200 || res?.status === 201) {
        const payload = res.data?.data ?? res.data;
        const rawList = extractWorkspaceList(payload);
        const adapted = adaptWorkspaceList(rawList);
        const count = payload?.count ?? payload?.total_count ?? adapted.length;
        setWorkspaces(adapted);
        setTotalCount(count);
        setWorkspacesAtom({ workspaces: adapted, total_count: count });
      } else {
        setWorkspaces([]);
        setTotalCount(0);
        setWorkspacesAtom({ workspaces: [], total_count: 0 });
        setError(res?.data?.data?.general_error || res?.data?.message || CAMPAIGN_COPY.errors.loadList);
      }
    } catch (err) {
      setWorkspaces([]);
      setTotalCount(0);
      setWorkspacesAtom({ workspaces: [], total_count: 0 });
      setError(
        err?.response?.data?.data?.general_error
        || err?.response?.data?.message
        || err?.message
        || CAMPAIGN_COPY.errors.loadList
      );
    } finally {
      loadedAccountRef.current = accountId;
      setIsLoading(false);
    }
  }, [accountId, search, setWorkspacesAtom]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  return {
    workspaces,
    totalCount,
    isLoading,
    error,
    search,
    setSearch,
    refetch: fetchWorkspaces,
  };
}
